import React, { useState, useEffect } from 'react';
import { useRecoilValue } from 'recoil';
import GlobalPopupContainerCenter from '../globalPopup/GlobalPopupContainerCenter';
import { userModelState } from '../../state/atoms/userModel';
import { getUserCurrencyLabel, getUserCurrencySymbol, updateUserPreferredCurrency, getUserPreferredCurrency, CURRENCY_CONFIG } from '../../utils/userCurrencyUtils';
import { 
    CurrencyList, 
    CurrencyItem, 
    CurrencySymbol, 
    CurrencyLabel, 
    CurrencyText, 
    HeaderText
} from './CurrencySelectionPopupStyling';

interface CurrencySelectionPopupProps {
    isOpen: boolean;
    onClose: () => void;
}

const CurrencySelectionPopup: React.FC<CurrencySelectionPopupProps> = ({ isOpen, onClose }) => {
    const userModel = useRecoilValue(userModelState);
    const [selectedCurrency, setSelectedCurrency] = useState<string>('aud');
    const [isLoading, setIsLoading] = useState(false);
    const [sortedCurrencies, setSortedCurrencies] = useState<[string, { symbol: string; label: string; text: string }][]>([]);

    // Get current currency using the full detection system (user preference → cache → browser detection → fallback)
    const currentCurrency = getUserPreferredCurrency(userModel);

    useEffect(() => {
        if (isOpen) {
            setSelectedCurrency(currentCurrency);
            // Sort currencies only when popup opens - selected currency first, then alphabetical
            const sorted = Object.entries(CURRENCY_CONFIG).sort(([a], [b]) => {
                if (a === currentCurrency) return -1;
                if (b === currentCurrency) return 1;
                return a.localeCompare(b);
            });
            setSortedCurrencies(sorted);
        }
    }, [isOpen, currentCurrency]);

    const handleCurrencySelect = (currencyCode: string) => {
        setSelectedCurrency(currencyCode);
    };

    const handleSave = async () => {
        if (selectedCurrency === currentCurrency) {
            onClose();
            return;
        }

        setIsLoading(true);
        try {
            const result = await updateUserPreferredCurrency(selectedCurrency);
            // For logged-out users, result will be null, but that's fine
            // The currency preference is still saved to cache/cookies
            onClose();
        } catch (error) {
            console.error('Failed to update currency preference:', error);
            // Still close the popup even if save fails
            onClose();
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;
    return (
        <GlobalPopupContainerCenter
            closePopup={onClose}
            handleSave={handleSave}
            disable={isLoading}
            isContinueBtnLoading={isLoading}
            btnText="Save"
            desktopWidth="400px"
            height="fit-content"
            desktopHeight="560px"
            noBackBtn={false}
            backBtnType="Close"
        >
            <HeaderText>Choose a currency</HeaderText>
            
            <CurrencyList>
                {sortedCurrencies.map(([code, config]) => (
                    <CurrencyItem
                        key={code}
                        onClick={() => handleCurrencySelect(code)}
                        isSelected={selectedCurrency === code}
                    >
                        <CurrencySymbol>{config.symbol}</CurrencySymbol>
                        <div>
                            <CurrencyLabel>{config.label}</CurrencyLabel>
                            <CurrencyText>{config.text}</CurrencyText>
                        </div>
                    </CurrencyItem>
                ))}
            </CurrencyList>
        </GlobalPopupContainerCenter>
    );
};

export default CurrencySelectionPopup;
